import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { richiesteDocApi, allegatiTecniciApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Upload, CheckCircle2, FileUp, Camera, FolderOpen } from 'lucide-react'
import toast from 'react-hot-toast'

interface RichiestaItem {
  richiesta_id: number
  tipo: string
  tipo_label: string
  richiesto_at?: string
  note?: string
}

interface RichiesteData {
  pendente: boolean
  tecnico_id?: number
  richieste: RichiestaItem[]
}

const LATI: [string, string] = ['Fronte', 'Retro']

function sessionKey(ids: number[]) {
  return `richiesta_doc_popup_${ids.sort().join('_')}`
}

export default function RichiestaDocPopup() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [visible, setVisible] = useState(false)
  const [data, setData] = useState<RichiesteData | null>(null)
  // uploadedSlots[docIdx][lato] = bool
  const [uploadedSlots, setUploadedSlots] = useState<boolean[][]>([])
  const [uploading, setUploading] = useState<boolean[][]>([])
  // 2 ref camera + 2 ref file per documento (max 2 doc × 2 lati)
  const cam00 = useRef<HTMLInputElement>(null); const file00 = useRef<HTMLInputElement>(null)
  const cam01 = useRef<HTMLInputElement>(null); const file01 = useRef<HTMLInputElement>(null)
  const cam10 = useRef<HTMLInputElement>(null); const file10 = useRef<HTMLInputElement>(null)
  const cam11 = useRef<HTMLInputElement>(null); const file11 = useRef<HTMLInputElement>(null)
  const camRefs = [[cam00, cam01], [cam10, cam11]]
  const fileRefs = [[file00, file01], [file10, file11]]

  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')

  const { data: fetched, refetch } = useQuery({
    queryKey: ['mia-richiesta-doc'],
    queryFn: () => richiesteDocApi.miaRichiesta().then(r => r.data as RichiesteData),
    enabled: isTecnico,
    staleTime: 60_000,
    refetchInterval: 300_000,
  })

  useEffect(() => {
    if (!fetched?.pendente || !fetched.richieste?.length) return
    const ids = fetched.richieste.map(r => r.richiesta_id)
    if (sessionStorage.getItem(sessionKey(ids))) return
    setData(fetched)
    const n = fetched.richieste.length
    setUploadedSlots(Array.from({ length: n }, () => [false, false]))
    setUploading(Array.from({ length: n }, () => [false, false]))
    setVisible(true)
  }, [fetched])

  const defer = () => {
    if (data) {
      const ids = data.richieste.map(r => r.richiesta_id)
      sessionStorage.setItem(sessionKey(ids), '1')
    }
    setVisible(false)
  }

  const handleFile = async (docIdx: number, lato: number, file: File) => {
    if (!data?.tecnico_id) return
    const richiesta = data.richieste[docIdx]
    setUploading(prev => {
      const u = prev.map(row => [...row])
      u[docIdx][lato] = true
      return u
    })
    try {
      await allegatiTecniciApi.upload(data.tecnico_id, file, richiesta.tipo)
      setUploadedSlots(prev => {
        const s = prev.map(row => [...row])
        s[docIdx][lato] = true
        return s
      })
      toast.success(`${richiesta.tipo_label} — ${LATI[lato]} caricato`)
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Errore upload')
    }
    setUploading(prev => {
      const u = prev.map(row => [...row])
      u[docIdx][lato] = false
      return u
    })
  }

  const completa = async () => {
    if (!data?.tecnico_id) return
    try {
      await Promise.all(
        data.richieste.map(r => richiesteDocApi.completa(data.tecnico_id!, r.richiesta_id))
      )
      const ids = data.richieste.map(r => r.richiesta_id)
      sessionStorage.setItem(sessionKey(ids), '1')
      qc.invalidateQueries({ queryKey: ['mia-richiesta-doc'] })
      setVisible(false)
      toast.success('Documenti inviati con successo!')
      refetch()
    } catch {
      toast.error('Errore completamento richiesta')
    }
  }

  if (!visible || !data?.richieste?.length) return null

  const tuttiCaricati = uploadedSlots.every(row => row.every(Boolean))
  const fmtData = (d?: string) => { try { return new Date(d!).toLocaleDateString('it-IT') } catch { return d ?? '' } }
  const firstNote = data.richieste.find(r => r.note)?.note
  const firstDate = data.richieste[0]?.richiesto_at

  return (
    <>
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9996,
        background: 'rgba(0,0,0,0.50)', backdropFilter: 'blur(3px)',
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', zIndex: 9997,
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: '#fff', borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        maxWidth: 500, width: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ background: '#1E40AF', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 1 }}>
          <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 8, padding: 6, display: 'flex', flexShrink: 0 }}>
            <FileUp size={18} color="#fff" />
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
              Richiesta document{data.richieste.length > 1 ? 'i' : 'o'}
              {data.richieste.length > 1 && (
                <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 8px', fontSize: 12 }}>
                  {data.richieste.length}
                </span>
              )}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
              Carica i documenti richiesti dall'amministrazione
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Info */}
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px' }}>
            {firstDate && (
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                Richiesto il {fmtData(firstDate)}
              </p>
            )}
            {firstNote && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#374151', fontStyle: 'italic' }}>
                Nota: {firstNote}
              </p>
            )}
          </div>

          {/* Un blocco per ogni documento richiesto */}
          {data.richieste.map((richiesta, docIdx) => (
            <div key={richiesta.richiesta_id} style={{
              border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
            }}>
              {/* Intestazione documento */}
              <div style={{
                background: '#f8fafc', padding: '10px 14px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#1e40af' }}>
                  {richiesta.tipo_label}
                </span>
                {uploadedSlots[docIdx]?.every(Boolean) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#16a34a' }}>
                    <CheckCircle2 size={13} /> Completato
                  </span>
                )}
              </div>

              {/* Slot fronte/retro */}
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {LATI.map((lato, latoIdx) => {
                  const isUp = uploading[docIdx]?.[latoIdx]
                  const isDone = uploadedSlots[docIdx]?.[latoIdx]
                  const camRef = camRefs[docIdx]?.[latoIdx]
                  const fileRef = fileRefs[docIdx]?.[latoIdx]
                  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const f = e.target.files?.[0]; if (f) handleFile(docIdx, latoIdx, f); e.target.value = ''
                  }
                  return (
                    <div key={latoIdx}>
                      {camRef && (
                        <input ref={camRef} type="file" accept="image/*" capture="environment"
                          style={{ display: 'none' }} onChange={onChange} />
                      )}
                      {fileRef && (
                        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                          style={{ display: 'none' }} onChange={onChange} />
                      )}

                      {isDone ? (
                        <div style={{
                          width: '100%', padding: '10px 12px', borderRadius: 8,
                          border: '2px solid #16a34a', background: '#f0fdf4',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                          <CheckCircle2 size={18} color="#16a34a" />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>{lato}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Caricato ✓</div>
                          </div>
                        </div>
                      ) : isUp ? (
                        <div style={{
                          width: '100%', padding: '10px 12px', borderRadius: 8,
                          border: '2px dashed #93c5fd', background: '#f8faff',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                          <div style={{ width: 18, height: 18, border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', flexShrink: 0, animation: 'spin 1s linear infinite' }} />
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{lato} — caricamento...</div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => camRef?.current?.click()}
                            style={{
                              flex: 1, padding: '9px 10px', borderRadius: 8,
                              border: '2px dashed #93c5fd', background: '#f0f9ff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                              cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#1d4ed8',
                            }}
                          >
                            <Camera size={15} /> {lato} — Scatta
                          </button>
                          <button
                            onClick={() => fileRef?.current?.click()}
                            style={{
                              flex: 1, padding: '9px 10px', borderRadius: 8,
                              border: '2px dashed #cbd5e1', background: '#f8fafc',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                              cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#64748b',
                            }}
                          >
                            <FolderOpen size={15} /> Scegli file
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Azioni */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={completa}
              disabled={!tuttiCaricati}
              style={{
                flex: 2, padding: '11px 0', borderRadius: 8, border: 'none',
                background: tuttiCaricati ? '#1E40AF' : '#e5e7eb',
                color: tuttiCaricati ? '#fff' : '#9ca3af',
                fontSize: 13, fontWeight: 700,
                cursor: tuttiCaricati ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <CheckCircle2 size={14} />Completa e invia
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
